import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  input,
  signal,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';
import { formatProfileTimeFormatConstants } from '@foxera/feature/settings/utils/branch.constants.utils';
import { FoxeraDialogComponent } from '@foxera/shared/components/foxera-dialog/foxera-dialog.component';
import { FoxeraDrawerFullComponent } from '@foxera/shared/components/foxera-drawer-full/foxera-drawer-full.component';
import { FoxeraDatePickerComponent } from '@foxera/shared/components/foxera-datepicker/foxera-datepicker.component';
import { FoxeraEmailInputComponent } from '@foxera/shared/components/foxera-email-input/foxera-email-input.component';
import { FoxeraInputTextComponent } from '@foxera/shared/components/foxera-input-text/foxera-input-text.component';
import { FoxeraPhoneInputComponent } from '@foxera/shared/components/foxera-phone-input/foxera-phone-input.component';
import { FoxeraProfileInputComponent } from '@foxera/shared/components/foxera-profile-input/foxera-profile-input.component';
import { FoxeraSelectComponent } from '@foxera/shared/components/foxera-select/foxera-select.component';
import { UnsubscribeDirective } from '@foxera/shared/directives/unsubscribe.directive';
import { IProfileData, IProfileUpdateData } from '@foxera/shared/interfaces/profile.interface';
import { ProfileService } from '@foxera/shared/services/profile.service';
import { CustomToastService } from '@foxera/shared/services/custom-toast.service';
import { TenantService } from '@foxera/feature/tenant/services/tenant.service';
import { ButtonModule } from 'primeng/button';
import { takeUntil } from 'rxjs';

@Component({
  selector: 'app-profile-dialog',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    FoxeraDialogComponent,
    FoxeraDrawerFullComponent,
    FoxeraProfileInputComponent,
    FoxeraInputTextComponent,
    FoxeraPhoneInputComponent,
    FoxeraEmailInputComponent,
    FoxeraDatePickerComponent,
    FoxeraSelectComponent,
  ],
  templateUrl: './profile-dialog.component.html',
  styleUrls: ['./profile-dialog.component.css']
})
export class ProfileDialogComponent extends UnsubscribeDirective implements OnInit, OnChanges {
  @Input() isVisible: boolean = false;
  readonly profileData = input<IProfileData | null>(null);

  @Output() visibilityChange = new EventEmitter<boolean>();

  profileForm!: FormGroup;
  profileImageFile: File | null = null;
  isSaving: boolean = false;
  originalTimeFormat: string = '';

  // Time format options
  public timeFormatOptions = signal<Array<{ label: string; value: string }>>([]);
  public isLoadingConstants = signal<boolean>(false);

  // Mobile detection
  private breakpointObserver = inject(BreakpointObserver);
  isMobile = signal(false);

  constructor(
    private fb: FormBuilder,
    private customToastService: CustomToastService,
    private profileService: ProfileService,
    private tenantService: TenantService
  ) {
    super();
  }

  ngOnInit(): void {
    this.initializeForm();
    this.loadProfileConstants();
    
    // Detect mobile breakpoint
    this.breakpointObserver
      .observe(['(max-width: 768px)'])
      .subscribe(result => {
        this.isMobile.set(result.matches);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['profileData'] && this.profileForm && this.profileData()) {
      this.patchFormValues();
    }

    // Load constants when dialog opens
    if (changes['isVisible'] && changes['isVisible'].currentValue && this.isVisible) {
      this.loadProfileConstants();
    }

    // Reset form when dialog is closed
    if (
      changes['isVisible'] &&
      !changes['isVisible'].currentValue &&
      this.profileForm
    ) {
      this.profileForm.reset();
      this.profileImageFile = null;
      this.originalTimeFormat = '';
    }
  }

  private initializeForm(): void {
    this.profileForm = this.fb.group({
      firstName: ['', [Validators.required, Validators.minLength(2)]],
      lastName: ['', [Validators.required, Validators.minLength(2)]],
      phoneNumber: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      birthdate: [null],
      timeFormat: ['', [Validators.required]],
    });

    if (this.profileData()) {
      this.patchFormValues();
    }
  }

  private loadProfileConstants(): void {
    // Only load constants if a tenant is selected
    const selectedTenant = this.tenantService.getSelectedTenantFromStorage();
    if (!selectedTenant?.id) {
      this.timeFormatOptions.set([]);
      this.isLoadingConstants.set(false);
      return;
    }

    this.isLoadingConstants.set(true);
    this.profileService
      .getProfileConstants()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (constants) => {
          const formattedTimeFormats = formatProfileTimeFormatConstants(constants);
          this.timeFormatOptions.set(formattedTimeFormats);
          this.isLoadingConstants.set(false);
        },
        error: () => {
          this.timeFormatOptions.set([]);
          this.isLoadingConstants.set(false);
        },
      });
  }

  private patchFormValues(): void {
    const profileData = this.profileData();
    if (!profileData) return;

    const timeFormat = profileData.timeFormat || '';
    this.originalTimeFormat = timeFormat;

    this.profileForm.patchValue({
      firstName: profileData.firstName || '',
      lastName: profileData.lastName || '',
      phoneNumber: profileData.phoneNumber || '',
      email: profileData.email || '',
      birthdate: profileData.birthdate
        ? new Date(profileData.birthdate)
        : null,
      timeFormat: timeFormat,
    });

    // Disable email field to prevent editing
    this.profileForm.get('email')?.disable();
  }

  onFileSelected(file: File | null): void {
    this.profileImageFile = file;
  }

  onValidationError(error: string): void {
    this.customToastService.showError(error);
  }

  close(): void {
    this.visibilityChange.emit(false);
  }

  save(): void {
    if (this.profileForm.invalid) {
      this.profileForm.markAllAsTouched();
      this.customToastService.showError('Please fill in all required fields correctly.');
      return;
    }

    this.isSaving = true;

    const formValues = this.profileForm.getRawValue();
    const profileData = this.profileData();

    const profileUpdateData: IProfileUpdateData = {
      firstName: formValues.firstName ?? '',
      lastName: formValues.lastName ?? '',
      phoneNumber: formValues.phoneNumber ?? '',
      email: formValues.email ?? profileData?.email ?? '',
      birthdate: formValues.birthdate ?? null,
      profileImagePath: profileData?.profileImagePath ?? null,
      timeFormat: formValues.timeFormat ?? '',
    };

    const timeFormatChanged = profileUpdateData.timeFormat !== this.originalTimeFormat;

    this.profileService
      .updateProfile(profileData?.id ?? '', profileUpdateData, this.profileImageFile ?? undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.customToastService.showSuccess('Profile updated');
          this.close();

          if (timeFormatChanged) {
            window.location.reload();
          }
        },
        error: () => {
          this.isSaving = false;
        },
      });
  }

  get firstName(): string {
    return this.profileForm.get('firstName')?.value || '';
  }

  get lastName(): string {
    return this.profileForm.get('lastName')?.value || '';
  }

}
